import { action, makeObservable, observable, runInAction } from "mobx";
import { Dayjs } from "dayjs";
import PodRevenueDashboardAPI from "../../api/dashboards/PodRevenueDashboardAPI";
import { FilterValue } from "../../../../models/Filters/FilterValue";
import {
  defaultPodRevenueAppliedFilters,
  PodRevenueAppliedFilters,
  PodRevenueSortBy,
  PodRevenueSortType,
} from "../../../../models/Dashboard/PodRevenue/PodRevenueFilters";
import {
  PodRevenueLineRow,
  PodRevenueOrderRow,
  PodRevenueProductRow,
  PodRevenueSummaryResult,
} from "../../../../models/Dashboard/PodRevenue/PodRevenueSummary";

const errMsg = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

interface PagedState<T> {
  rows: T[];
  total: number;
  page: number;
  pages: number;
  sortBy: PodRevenueSortBy;
  sortType: PodRevenueSortType;
}

type LinesState = PagedState<PodRevenueLineRow>;
type OrdersState = PagedState<PodRevenueOrderRow>;

const emptyLines = <T = PodRevenueLineRow>(): PagedState<T> => ({
  rows: [],
  total: 0,
  page: 1,
  pages: 0,
  sortBy: "order_date",
  sortType: "DESC",
});

/**
 * Mirrors RedemptionDashboardStore: applied filters (date + order id +
 * customer), summary, product listing, line listing, and a product drawer.
 */
export class PodRevenueDashboardStore {
  api: PodRevenueDashboardAPI;
  applied: PodRevenueAppliedFilters = defaultPodRevenueAppliedFilters();

  draftFrom: Dayjs = this.applied.from;
  draftTo: Dayjs = this.applied.to;
  selectedOrderIds: FilterValue[] = [];
  selectedCustomers: FilterValue[] = [];
  orderQuery = "";
  customerQuery = "";
  orderLoading = false;
  searchText = "";
  filterError: string | null = null;
  private orderSearchSeq = 0;

  summaryLoading = false;
  productsLoading = false;
  linesLoading = false;
  drawerLoading = false;
  summaryError: string | null = null;
  productsError: string | null = null;
  linesError: string | null = null;
  drawerError: string | null = null;

  summary: PodRevenueSummaryResult | null = null;
  products: PodRevenueProductRow[] = [];
  productsTotal = 0;
  productsPage = 1;
  productsPages = 0;
  productsSortBy: PodRevenueSortBy = "gross_revenue";
  productsSortType: PodRevenueSortType = "DESC";

  /** Main listing: one row per order. */
  lines: OrdersState = emptyLines<PodRevenueOrderRow>();

  drawerOpen = false;
  drawerProduct: PodRevenueProductRow | null = null;
  drawerOrder: PodRevenueOrderRow | null = null;
  drawerLines: LinesState = emptyLines();

  pageSize = 20;

  private summarySeq = 0;
  private productsSeq = 0;
  private linesSeq = 0;
  private drawerSeq = 0;

  constructor() {
    this.api = new PodRevenueDashboardAPI();
    makeObservable(this, {
      applied: observable.ref,
      draftFrom: observable.ref,
      draftTo: observable.ref,
      selectedOrderIds: observable.ref,
      selectedCustomers: observable.ref,
      orderQuery: observable,
      customerQuery: observable,
      orderLoading: observable,
      searchText: observable,
      filterError: observable,
      summaryLoading: observable,
      productsLoading: observable,
      linesLoading: observable,
      drawerLoading: observable,
      summaryError: observable,
      productsError: observable,
      linesError: observable,
      drawerError: observable,
      summary: observable.ref,
      products: observable.ref,
      productsTotal: observable,
      productsPage: observable,
      productsPages: observable,
      productsSortBy: observable,
      productsSortType: observable,
      lines: observable.ref,
      drawerOpen: observable,
      drawerProduct: observable.ref,
      drawerOrder: observable.ref,
      drawerLines: observable.ref,
      setDraftFrom: action,
      setDraftTo: action,
      setSelectedOrderIds: action,
      setSelectedCustomers: action,
      setOrderQuery: action,
      setCustomerQuery: action,
      setSearchText: action,
      setFilterError: action,
      beginOrderSearch: action,
      finishOrderSearch: action,
      cancelOrderSearch: action,
      clearDraft: action,
      applyFilters: action,
      refresh: action,
      fetchSummary: action,
      fetchProducts: action,
      fetchLines: action,
      fetchDrawer: action,
      setProductsPage: action,
      setProductsSort: action,
      setLinesPage: action,
      setLinesSort: action,
      openProduct: action,
      openOrder: action,
      closeDrawer: action,
      setDrawerPage: action,
      setDrawerSort: action,
    });
  }

  setDraftFrom = (value: Dayjs) => {
    this.draftFrom = value;
  };

  setDraftTo = (value: Dayjs) => {
    this.draftTo = value;
  };

  setSelectedOrderIds = (items: FilterValue[]) => {
    this.selectedOrderIds = items;
  };

  setSelectedCustomers = (items: FilterValue[]) => {
    this.selectedCustomers = items;
  };

  setOrderQuery = (value: string) => {
    this.orderQuery = value;
  };

  setCustomerQuery = (value: string) => {
    this.customerQuery = value;
  };

  setSearchText = (value: string) => {
    this.searchText = value;
  };

  setFilterError = (message: string | null) => {
    this.filterError = message;
  };

  beginOrderSearch = (): number => {
    this.orderSearchSeq += 1;
    this.orderLoading = true;
    return this.orderSearchSeq;
  };

  finishOrderSearch = (seq: number, error?: string) => {
    if (seq !== this.orderSearchSeq) return;
    this.orderLoading = false;
    if (error) this.filterError = error;
  };

  cancelOrderSearch = () => {
    this.orderSearchSeq += 1;
    this.orderLoading = false;
  };

  clearDraft = () => {
    const cleared = defaultPodRevenueAppliedFilters();
    this.draftFrom = cleared.from;
    this.draftTo = cleared.to;
    this.selectedOrderIds = [];
    this.selectedCustomers = [];
    this.searchText = "";
    this.filterError = null;
  };

  applyFilters = async (applied: PodRevenueAppliedFilters) => {
    this.applied = applied;
    this.productsPage = 1;
    this.lines = { ...this.lines, page: 1 };
    this.drawerLines = { ...this.drawerLines, page: 1 };
    await this.refresh();
  };

  refresh = async () => {
    await Promise.all([
      this.fetchSummary(),
      this.fetchProducts(),
      this.fetchLines(),
      this.drawerOpen ? this.fetchDrawer() : Promise.resolve(),
    ]);
  };

  fetchSummary = async () => {
    const seq = ++this.summarySeq;
    this.summaryLoading = true;
    this.summaryError = null;
    try {
      const result = await this.api.getSummary(this.applied);
      if (seq !== this.summarySeq) return;
      runInAction(() => {
        this.summary = result;
      });
    } catch (error) {
      if (seq !== this.summarySeq) return;
      runInAction(() => {
        this.summary = null;
        this.summaryError = errMsg(error, "Failed to load summary.");
      });
    } finally {
      if (seq === this.summarySeq) {
        runInAction(() => {
          this.summaryLoading = false;
        });
      }
    }
  };

  fetchProducts = async () => {
    const seq = ++this.productsSeq;
    this.productsLoading = true;
    this.productsError = null;
    try {
      const result = await this.api.getProducts(
        this.applied,
        this.productsPage,
        this.pageSize,
        this.productsSortBy,
        this.productsSortType,
      );
      if (seq !== this.productsSeq) return;
      runInAction(() => {
        this.products = result.data?.list || [];
        this.productsTotal = result.data?.total_count || 0;
        this.productsPages = result.data?.total_pages || 0;
      });
    } catch (error) {
      if (seq !== this.productsSeq) return;
      runInAction(() => {
        this.products = [];
        this.productsTotal = 0;
        this.productsPages = 0;
        this.productsError = errMsg(error, "Failed to load products.");
      });
    } finally {
      if (seq === this.productsSeq) {
        runInAction(() => {
          this.productsLoading = false;
        });
      }
    }
  };

  private loadLines = async (
    state: LinesState,
    productId?: number,
    orderId?: number,
  ): Promise<LinesState> => {
    const result = await this.api.getLines(
      orderId
        ? { ...this.applied, order_id: [orderId], customer_id: [], q: "" }
        : this.applied,
      state.page,
      this.pageSize,
      state.sortBy,
      state.sortType,
      productId,
    );
    return {
      ...state,
      rows: result.data?.list || [],
      total: result.data?.total_count || 0,
      pages: result.data?.total_pages || 0,
    };
  };

  fetchLines = async () => {
    const seq = ++this.linesSeq;
    this.linesLoading = true;
    this.linesError = null;
    try {
      const result = await this.api.getOrders(
        this.applied,
        this.lines.page,
        this.pageSize,
        this.lines.sortBy,
        this.lines.sortType,
      );
      const next: OrdersState = {
        ...this.lines,
        rows: result.data?.list || [],
        total: result.data?.total_count || 0,
        pages: result.data?.total_pages || 0,
      };
      if (seq !== this.linesSeq) return;
      runInAction(() => {
        this.lines = next;
      });
    } catch (error) {
      if (seq !== this.linesSeq) return;
      runInAction(() => {
        this.lines = { ...this.lines, rows: [], total: 0, pages: 0 };
        this.linesError = errMsg(error, "Failed to load orders.");
      });
    } finally {
      if (seq === this.linesSeq) {
        runInAction(() => {
          this.linesLoading = false;
        });
      }
    }
  };

  fetchDrawer = async () => {
    const product = this.drawerProduct;
    const order = this.drawerOrder;
    if (!product && !order) return;
    const seq = ++this.drawerSeq;
    this.drawerLoading = true;
    this.drawerError = null;
    try {
      const next = await this.loadLines(
        this.drawerLines,
        order ? undefined : product?.product_id,
        order?.order_id,
      );
      if (seq !== this.drawerSeq) return;
      runInAction(() => {
        this.drawerLines = next;
      });
    } catch (error) {
      if (seq !== this.drawerSeq) return;
      runInAction(() => {
        this.drawerLines = {
          ...this.drawerLines,
          rows: [],
          total: 0,
          pages: 0,
        };
        this.drawerError = errMsg(
          error,
          order
            ? "Failed to load order products."
            : "Failed to load product lines.",
        );
      });
    } finally {
      if (seq === this.drawerSeq) {
        runInAction(() => {
          this.drawerLoading = false;
        });
      }
    }
  };

  setProductsPage = async (page: number) => {
    this.productsPage = Math.max(1, page);
    await this.fetchProducts();
  };

  setProductsSort = async (sort_by: PodRevenueSortBy) => {
    if (this.productsSortBy === sort_by) {
      this.productsSortType = this.productsSortType === "DESC" ? "ASC" : "DESC";
    } else {
      this.productsSortBy = sort_by;
      this.productsSortType = "DESC";
    }
    this.productsPage = 1;
    await this.fetchProducts();
  };

  private nextSort = <T>(
    state: PagedState<T>,
    sort_by: PodRevenueSortBy,
  ): PagedState<T> =>
    state.sortBy === sort_by
      ? {
          ...state,
          page: 1,
          sortType: state.sortType === "DESC" ? "ASC" : "DESC",
        }
      : { ...state, page: 1, sortBy: sort_by, sortType: "DESC" };

  setLinesPage = async (page: number) => {
    this.lines = { ...this.lines, page: Math.max(1, page) };
    await this.fetchLines();
  };

  setLinesSort = async (sort_by: PodRevenueSortBy) => {
    this.lines = this.nextSort(this.lines, sort_by);
    await this.fetchLines();
  };

  openProduct = async (product: PodRevenueProductRow) => {
    this.drawerProduct = product;
    this.drawerOrder = null;
    this.drawerOpen = true;
    this.drawerLines = emptyLines();
    await this.fetchDrawer();
  };

  openOrder = async (order: PodRevenueOrderRow) => {
    this.drawerOrder = order;
    this.drawerProduct = null;
    this.drawerOpen = true;
    this.drawerLines = { ...emptyLines(), sortBy: "gross_revenue" };
    await this.fetchDrawer();
  };

  closeDrawer = () => {
    this.drawerSeq += 1;
    this.drawerOpen = false;
    this.drawerLoading = false;
    this.drawerError = null;
  };

  setDrawerPage = async (page: number) => {
    this.drawerLines = { ...this.drawerLines, page: Math.max(1, page) };
    await this.fetchDrawer();
  };

  setDrawerSort = async (sort_by: PodRevenueSortBy) => {
    this.drawerLines = this.nextSort(this.drawerLines, sort_by);
    await this.fetchDrawer();
  };
}
